'use client';

import { useClerk, useSignIn as useClerkSignIn } from '@clerk/nextjs';
import { isClerkAPIResponseError } from '@clerk/nextjs/errors';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { persistLastAuthenticationStrategy } from '@/app/(auth)/_utils/auth-last-authentication-strategy';
import { resetStaleOAuthState } from '@/app/(auth)/_utils/reset-stale-oauth-state';
import { DEFAULT_LOGGED_PAGE } from '@/infra/constants/routes';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { resolvePostAuthRedirectUrl } from '@/utils/resolve-post-sign-up-redirect-url';
import { type SecondFactorStrategy, type SignInStep, useSignInFlowStore } from './use-sign-in-flow-store';
interface UseSignInReturn {
  errors: ReturnType<typeof useClerkSignIn>['errors'];
  fetchStatus: ReturnType<typeof useClerkSignIn>['fetchStatus'];
  goToStep: (step: SignInStep) => void;
  isRedirecting: boolean;
  onForgotPassword: () => void;
  onGoBack: () => void;
  onPrepareChooseStrategy: (identifier: string) => Promise<boolean>;
  onPrepareEmailCodeFirstFactor: () => Promise<void>;
  onResendCode: () => Promise<void>;
  onResendResetCode: () => Promise<void>;
  onSendResetCode: (identifier?: string) => Promise<void>;
  onSignInIdentifier: (identifier: string) => Promise<void>;
  onSignInWithGoogle: () => Promise<void>;
  onSignUpSubmitCode: (code: string) => Promise<boolean>;
  onSubmitNewPassword: (password: string) => Promise<void>;
  onSubmitPassword: (password: string, identifier?: string) => Promise<void>;
  onUseBackupCode: () => void;
  onVerifyResetCode: (code: string) => Promise<void>;
  secondFactorStrategy: SecondFactorStrategy;
  signIn: ReturnType<typeof useClerkSignIn>['signIn'];
  step: SignInStep;
}
type EmailCodeParams = {
  emailAddressId: string;
} | {
  emailAddress: string;
} | Record<string, never>;
export function useSignIn(): UseSignInReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  const clerk = useClerk();
  const {
    signIn,
    errors,
    fetchStatus
  } = useClerkSignIn();
  const {
    trackSignInStarted,
    trackSignInEmailReturnClicked,
    trackSignInMfaStarted,
    trackSignInMfaCompleted,
    trackSignInMfaBackupCodeSelected,
    trackSignInCompleted,
    trackSignInForgotPasswordOpened,
    trackSignInResetCodeRequested,
    trackSignInResetCodeSubmitted,
    trackSignInResetCodeResent,
    trackSignInNewPasswordSubmitted,
    trackSignInCodeResent,
    trackAuthError
  } = useAuthEvents();
  const step = useSignInFlowStore(s => s.step);
  const storedIdentifier = useSignInFlowStore(s_0 => s_0.identifier);
  const firstFactorStrategy = useSignInFlowStore(s_1 => s_1.firstFactorStrategy);
  const firstFactorMethod = useSignInFlowStore(s_2 => s_2.firstFactorMethod);
  const secondFactorStrategy = useSignInFlowStore(s_3 => s_3.secondFactorStrategy);
  const isRedirecting = useSignInFlowStore(s_4 => s_4.isRedirecting);
  const isFormVisible = useSignInFlowStore(s_5 => s_5.isFormVisible);
  const forgotPasswordOrigin = useSignInFlowStore(s_6 => s_6.forgotPasswordOrigin);
  const onChange = useSignInFlowStore(s_7 => s_7.onChange);
  function goToStep(nextStep: SignInStep): void {
    onChange({
      step: nextStep
    });
  }
  async function finalizeSignIn(): Promise<void> {
    await signIn.finalize({
      navigate: () => {
        router.push(resolvePostAuthRedirectUrl(searchParams, DEFAULT_LOGGED_PAGE));
      }
    });
  }
  function resolveCompletedAuthStrategy(): string {
    if (firstFactorMethod === 'google') {
      return 'oauth_google';
    }
    if (firstFactorStrategy === 'email_code') {
      return 'email_code';
    }
    return 'password';
  }
  async function completeSignIn(strategy: string): Promise<void> {
    persistLastAuthenticationStrategy(strategy);
    trackSignInCompleted();
    onChange({
      isRedirecting: true
    });
    await finalizeSignIn();
  }
  function handleClerkError(error: unknown): void {
    if (isClerkAPIResponseError(error)) {
      const message = error.errors[0]?.longMessage || error.errors[0]?.message || 'An unexpected error occurred';
      toast.error('Something went wrong', {
        description: message,
        position: 'bottom-center'
      });
    }
  }
  async function onSignInIdentifier(identifier: string): Promise<void> {
    try {
      trackSignInStarted('email');
      const {
        error: error_1
      } = await signIn.create({
        identifier
      });
      if (error_1) {
        trackAuthError('sign_in', 'start', error_1.code, {
          method: 'email'
        });
        toast.error('Something went wrong', {
          description: error_1.longMessage || error_1.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
        return;
      }
      onChange({
        identifier,
        step: 'verifications'
      });
    } catch (error_0) {
      if (isClerkAPIResponseError(error_0)) {
        trackAuthError('sign_in', 'start', error_0.errors[0]?.code, {
          method: 'email'
        });
      }
      handleClerkError(error_0);
    }
  }
  async function onSignInWithGoogle(): Promise<void> {
    try {
      trackSignInStarted('google');
      const {
        error: resetError
      } = await resetStaleOAuthState({
        signIn,
        clerkClient: clerk.client
      });
      if (resetError) {
        trackAuthError('sign_in', 'start', resetError.code, {
          method: 'google'
        });
        toast.error('Something went wrong', {
          description: resetError.longMessage || resetError.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
        return;
      }
      const redirectUrl = resolvePostAuthRedirectUrl(searchParams, DEFAULT_LOGGED_PAGE);
      const {
        error: error_3
      } = await signIn.sso({
        strategy: 'oauth_google',
        redirectUrl,
        redirectCallbackUrl: `/sign-in/sso-callback?${getAllowlistedSearchParams(searchParams)}`
      });
      if (error_3) {
        await resetStaleOAuthState({
          signIn,
          clerkClient: clerk.client
        });
        trackAuthError('sign_in', 'start', error_3.code, {
          method: 'google'
        });
        toast.error('Something went wrong', {
          description: error_3.longMessage || error_3.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
      }
    } catch (error_2) {
      await resetStaleOAuthState({
        signIn,
        clerkClient: clerk.client
      });
      if (isClerkAPIResponseError(error_2)) {
        trackAuthError('sign_in', 'start', error_2.errors[0]?.code, {
          method: 'google'
        });
      }
      handleClerkError(error_2);
    }
  }
  function resolveEmailCodeParams(): EmailCodeParams {
    const emailCodeFactor = signIn.supportedFirstFactors?.find(f => f.strategy === 'email_code');
    const emailAddressId = emailCodeFactor && 'emailAddressId' in emailCodeFactor ? (emailCodeFactor as {
      emailAddressId: string;
    }).emailAddressId : undefined;
    const emailAddress = storedIdentifier?.includes('@') ? storedIdentifier : signIn.identifier ?? undefined;
    if (emailAddressId) {
      return {
        emailAddressId
      };
    }
    if (emailAddress) {
      return {
        emailAddress
      };
    }
    return {};
  }
  async function prepareSecondFactor(): Promise<boolean> {
    const hasTOTP = signIn.supportedSecondFactors?.some(f_0 => f_0.strategy === 'totp');
    if (hasTOTP) {
      trackSignInMfaStarted('totp');
      onChange({
        secondFactorStrategy: 'totp'
      });
      goToStep('verifications');
      return true;
    }
    const emailCodeFactor_0 = signIn.supportedSecondFactors?.find(factor => factor.strategy === 'email_code');
    if (emailCodeFactor_0) {
      await signIn.mfa.sendEmailCode();
      trackSignInMfaStarted('email_code');
      onChange({
        secondFactorStrategy: 'email_code'
      });
      goToStep('verifications');
      return true;
    }
    const hasBackupCode = signIn.supportedSecondFactors?.some(f_1 => f_1.strategy === 'backup_code');
    if (hasBackupCode) {
      trackSignInMfaStarted('backup_code');
      onChange({
        secondFactorStrategy: 'backup_code'
      });
      goToStep('verifications');
      return true;
    }
    trackAuthError('sign_in', 'verifications', 'second_factor_not_available', {
      strategy: secondFactorStrategy
    });
    toast.error('Something went wrong', {
      description: 'Two-factor authentication is not available for this account. Please contact support.',
      position: 'bottom-center'
    });
    return false;
  }
  async function handlePasswordSignInStatus(): Promise<void> {
    if (signIn.status === 'complete') {
      await completeSignIn(resolveCompletedAuthStrategy());
      return;
    }
    if (signIn.status === 'needs_new_password') {
      goToStep('reset-password');
      return;
    }
    if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust') {
      await prepareSecondFactor();
    }
  }
  async function onSubmitPassword(password: string, identifier_0?: string): Promise<void> {
    try {
      const id = identifier_0 ?? storedIdentifier;
      if (identifier_0) {
        onChange({
          identifier: identifier_0
        });
      }
      const {
        error: error_5
      } = await signIn.password({
        identifier: id,
        password
      });
      if (error_5) {
        trackAuthError('sign_in', 'verifications', error_5.code, {
          method: 'email',
          strategy: 'password'
        });
        const description = error_5.code === 'strategy_for_user_invalid' ? 'This account uses Google sign-in. Please use the Google button to sign in.' : error_5.longMessage || error_5.message || 'An unexpected error occurred';
        toast.error('Something went wrong', {
          description,
          position: 'bottom-center'
        });
        return;
      }
      await handlePasswordSignInStatus();
    } catch (error_4) {
      if (isClerkAPIResponseError(error_4)) {
        trackAuthError('sign_in', 'verifications', error_4.errors[0]?.code, {
          method: 'email',
          strategy: 'password'
        });
      }
      handleClerkError(error_4);
    }
  }
  async function onPrepareChooseStrategy(identifier_1: string): Promise<boolean> {
    try {
      const {
        error: error_7
      } = await signIn.create({
        identifier: identifier_1
      });
      if (error_7) {
        toast.error('Something went wrong', {
          description: error_7.longMessage || error_7.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
        return false;
      }
      onChange({
        identifier: identifier_1
      });
      return true;
    } catch (error_6) {
      handleClerkError(error_6);
      return false;
    }
  }
  async function onPrepareEmailCodeFirstFactor(): Promise<void> {
    try {
      const params = resolveEmailCodeParams();
      if (Object.keys(params).length === 0) {
        trackAuthError('sign_in', 'choose_strategy', 'email_code_not_available', {
          strategy: 'email_code'
        });
        toast.error('Something went wrong', {
          description: 'Email code sign-in is not available for this account.',
          position: 'bottom-center'
        });
        return;
      }
      const {
        error: error_9
      } = await signIn.emailCode.sendCode(params);
      if (error_9) {
        trackAuthError('sign_in', 'choose_strategy', error_9.code, {
          strategy: 'email_code'
        });
        toast.error('Something went wrong', {
          description: error_9.longMessage || error_9.message || 'Email code sign-in is not available for this account.',
          position: 'bottom-center'
        });
        return;
      }
      onChange({
        firstFactorStrategy: 'email_code',
        step: 'verifications'
      });
    } catch (error_8) {
      if (isClerkAPIResponseError(error_8)) {
        trackAuthError('sign_in', 'choose_strategy', error_8.errors[0]?.code, {
          strategy: 'email_code'
        });
      }
      handleClerkError(error_8);
    }
  }
  function isFirstFactorEmailCodeFlow(): boolean {
    return firstFactorStrategy === 'email_code' && signIn.status === 'needs_first_factor';
  }
  async function onSignUpSubmitCode(code: string): Promise<boolean> {
    const isFirstFactorEmailCode = isFirstFactorEmailCodeFlow();
    try {
      if (isFirstFactorEmailCode) {
        const {
          error: error_11
        } = await signIn.emailCode.verifyCode({
          code
        });
        if (error_11) {
          trackAuthError('sign_in', 'verifications', error_11.code, {
            strategy: 'email_code'
          });
          toast.error('Something went wrong', {
            description: error_11.longMessage || error_11.message || 'An unexpected error occurred',
            position: 'bottom-center'
          });
          return false;
        }
      } else {
        let verification: Awaited<ReturnType<typeof signIn.mfa.verifyTOTP>>;
        if (secondFactorStrategy === 'totp') {
          verification = await signIn.mfa.verifyTOTP({
            code
          });
        } else if (secondFactorStrategy === 'backup_code') {
          verification = await signIn.mfa.verifyBackupCode({
            code
          });
        } else {
          verification = await signIn.mfa.verifyEmailCode({
            code
          });
        }
        if (verification.error) {
          trackAuthError('sign_in', 'verifications', verification.error.code, {
            strategy: secondFactorStrategy
          });
          toast.error('Something went wrong', {
            description: verification.error.longMessage || verification.error.message || 'An unexpected error occurred',
            position: 'bottom-center'
          });
          return false;
        }
      }
      if (signIn.status === 'complete') {
        if (!isFirstFactorEmailCode) {
          trackSignInMfaCompleted(secondFactorStrategy);
        }
        await completeSignIn(resolveCompletedAuthStrategy());
      } else if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust') {
        return await prepareSecondFactor();
      }
      return true;
    } catch (error_10) {
      if (isClerkAPIResponseError(error_10)) {
        trackAuthError('sign_in', 'verifications', error_10.errors[0]?.code, {
          strategy: secondFactorStrategy
        });
      }
      handleClerkError(error_10);
      return false;
    }
  }
  function onUseBackupCode(): void {
    trackSignInMfaBackupCodeSelected();
    onChange({
      secondFactorStrategy: 'backup_code'
    });
  }
  async function onResendCode(): Promise<void> {
    try {
      trackSignInCodeResent(firstFactorStrategy);
      if (isFirstFactorEmailCodeFlow()) {
        const params_0 = resolveEmailCodeParams();
        const {
          error: error_13
        } = await signIn.emailCode.sendCode(params_0);
        if (error_13) {
          trackAuthError('sign_in', 'verifications', error_13.code, {
            strategy: 'email_code'
          });
          toast.error('Something went wrong', {
            description: error_13.longMessage || error_13.message || 'An unexpected error occurred',
            position: 'bottom-center'
          });
        }
      } else {
        await signIn.mfa.sendEmailCode();
      }
    } catch (error_12) {
      if (isClerkAPIResponseError(error_12)) {
        trackAuthError('sign_in', 'verifications', error_12.errors[0]?.code, {
          strategy: 'email_code'
        });
      }
      handleClerkError(error_12);
    }
  }
  function onForgotPassword(): void {
    trackSignInForgotPasswordOpened();
    onChange({
      step: 'forgot-password',
      forgotPasswordOrigin: step === 'start' && isFormVisible ? 'start-email-form' : 'verifications'
    });
  }
  async function onSendResetCode(identifier_2?: string): Promise<void> {
    try {
      const id_0 = identifier_2 ?? storedIdentifier;
      trackSignInResetCodeRequested();
      const {
        error: createError
      } = await signIn.create({
        identifier: id_0
      });
      if (createError) {
        trackAuthError('sign_in', 'forgot_password', createError.code, {
          method: 'email'
        });
        toast.error('Something went wrong', {
          description: createError.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
        return;
      }
      const {
        error: sendCodeError
      } = await signIn.resetPasswordEmailCode.sendCode();
      if (sendCodeError) {
        trackAuthError('sign_in', 'forgot_password', sendCodeError.code, {
          method: 'email'
        });
        toast.error('Something went wrong', {
          description: sendCodeError.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
        return;
      }
      goToStep('reset-password');
    } catch (error_14) {
      if (isClerkAPIResponseError(error_14)) {
        trackAuthError('sign_in', 'forgot_password', error_14.errors[0]?.code, {
          method: 'email'
        });
      }
      handleClerkError(error_14);
    }
  }
  async function onVerifyResetCode(code_0: string): Promise<void> {
    try {
      trackSignInResetCodeSubmitted();
      await signIn.resetPasswordEmailCode.verifyCode({
        code: code_0
      });
    } catch (error_15) {
      if (isClerkAPIResponseError(error_15)) {
        trackAuthError('sign_in', 'reset_password', error_15.errors[0]?.code, {
          method: 'email'
        });
      }
      handleClerkError(error_15);
    }
  }
  async function onResendResetCode(): Promise<void> {
    try {
      trackSignInResetCodeResent();
      await signIn.resetPasswordEmailCode.sendCode();
    } catch (error_16) {
      if (isClerkAPIResponseError(error_16)) {
        trackAuthError('sign_in', 'reset_password', error_16.errors[0]?.code, {
          method: 'email'
        });
      }
      handleClerkError(error_16);
    }
  }
  function onGoBack(): void {
    if (step === 'forgot-password') {
      if (forgotPasswordOrigin === 'start-email-form') {
        trackSignInEmailReturnClicked('forgot_password', 'start_email_form');
        onChange({
          step: 'start',
          isFormVisible: true,
          forgotPasswordOrigin: null
        });
        return;
      }
      trackSignInEmailReturnClicked('forgot_password', 'verifications');
      onChange({
        step: 'verifications',
        forgotPasswordOrigin: null
      });
    } else if (step === 'reset-password') {
      trackSignInEmailReturnClicked('reset_password', 'forgot_password');
      goToStep('forgot-password');
    } else {
      trackSignInEmailReturnClicked('start', 'start');
      goToStep('start');
    }
  }
  async function onSubmitNewPassword(password_0: string): Promise<void> {
    try {
      trackSignInNewPasswordSubmitted();
      await signIn.resetPasswordEmailCode.submitPassword({
        password: password_0
      });
      if (signIn.status === 'complete') {
        await completeSignIn('password');
      }
    } catch (error_17) {
      if (isClerkAPIResponseError(error_17)) {
        trackAuthError('sign_in', 'reset_password', error_17.errors[0]?.code, {
          method: 'email'
        });
      }
      handleClerkError(error_17);
    }
  }
  return {
    step,
    isRedirecting,
    signIn,
    errors,
    fetchStatus,
    secondFactorStrategy,
    onSignInIdentifier,
    onSignInWithGoogle,
    onSubmitPassword,
    onPrepareChooseStrategy,
    onPrepareEmailCodeFirstFactor,
    onSignUpSubmitCode,
    onResendCode,
    onUseBackupCode,
    onForgotPassword,
    onSendResetCode,
    onVerifyResetCode,
    onResendResetCode,
    onGoBack,
    onSubmitNewPassword,
    goToStep
  };
}